import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { FormControl, ThreeDotsLoader } from "@atoms"
import { useRegistrationForm } from "./use-form"
import { useCreateMember, useVerifyTokenValidity } from "./queries"
import laparoliereLogo from "assets/laparoliere-logo.png"
import styles from "../styles.module.scss"
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Result,
  Row,
  Space,
  Typography,
} from "antd"
import { DatePicker } from "antd"
import { Controller } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Header } from "modules/layout/auth"

const Registration = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const typeParam = searchParams.get("type")?.toLowerCase()

  // If no token is present, redirect to email registration
  if (!token) {
    navigate("../email-registration")
  }

  const tokenValidity = useVerifyTokenValidity({
    token: token as string,
  })

  const createMember = useCreateMember()
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
  } = useRegistrationForm({
    defaultValues: {
      token: token as string,
      type: typeParam || "member",
    },
  })

  const onSubmit = handleSubmit((data) => {
    createMember.mutate(data as any, {
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (const key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  })

  if (tokenValidity.isLoading) {
    return <IsVerifying />
  }

  if (
    tokenValidity.isError &&
    tokenValidity.error?.response?.data.code === "member/email-exist"
  ) {
    return <Error error="email_exist_error" />
  }

  if (tokenValidity.isError) {
    return <Error />
  }

  if (createMember.isSuccess) {
    return (
      <Success
        hasPassword={!!watch("password")}
        isMember={createMember.variables?.type === "member"}
      />
    )
  }

  return (
    <Card
      style={{
        overflow: "hidden",
        maxWidth: "70ch",
      }}
    >
      <header>
        <img src={laparoliereLogo} alt="laparoliere" />
        <h1 className={`headline-heavy ${styles.banner}`}>
          {t("external.members.register")}
        </h1>
      </header>

      <Form
        layout="vertical"
        onFinish={onSubmit}
        style={{ marginTop: "var(--size-8)" }}
      >
        <FormControl>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={[
                  {
                    label: t("external.members.member_option"),
                    value: "member",
                  },
                  {
                    label: t("external.members.non_member_option"),
                    value: "non_member",
                  },
                ]}
                optionType="button"
                buttonStyle="solid"
                size="large"
              />
            )}
          />
        </FormControl>
        <Row justify="space-between" gutter={32}>
          <Col xs={24} md={12}>
            <Form.Item
              validateStatus={errors.first_name && "error"}
              help={errors.first_name?.message}
              name="first_name"
              label={t("global.first_name")}
              required
            >
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.first_name")}
                    size="large"
                    {...field}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.last_name && "error"}
              help={errors.last_name?.message}
              name="last_name"
              label={t("global.last_name")}
              required
            >
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.last_name")}
                    size="large"
                    {...field}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.birthdate && "error"}
              help={errors.birthdate?.message}
              name="birthdate"
              label={t("global.birthdate")}
            >
              <Controller
                name="birthdate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    placeholder={t("global.birthdate")}
                    size="large"
                    style={{ width: "100%" }}
                    onChange={(date, dateString) => field.onChange(dateString)}
                    value={field.value ? dayjs(field.value) : null}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.phone && "error"}
              help={errors.phone?.message}
              name="phone"
              label={t("global.phone")}
            >
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.phone")}
                    size="large"
                    type="tel"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value
                      if (
                        value.match(
                          /^(?:(?:\()?([0-9]{1,3})(?:\)?[-. ]?([0-9]{0,3})[-. ]?([0-9]{0,4}))?)?$/
                        ) ||
                        value === ""
                      ) {
                        field.onChange(value)
                      }
                    }}
                  />
                )}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              validateStatus={errors.address?.street_1 && "error"}
              help={errors.address?.street_1?.message}
              name="street_1"
              label={t("global.street_1")}
            >
              <Controller
                name="address.street_1"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.street_1")}
                    size="large"
                    {...field}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.address?.street_2 && "error"}
              help={errors.address?.street_2?.message}
              name="street_2"
              label={t("global.street_2")}
            >
              <Controller
                name="address.street_2"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.street_2")}
                    size="large"
                    {...field}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.address?.city && "error"}
              help={errors.address?.city?.message}
              name="city"
              label={t("global.city")}
            >
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("global.city")}
                    size="large"
                    {...field}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.address?.zip && "error"}
              help={errors.address?.zip?.message}
              name="zip"
              label={t("global.zip")}
            >
              <Controller
                name="address.zip"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="A1A 1A1"
                    size="large"
                    {...field}
                    onChange={(e) => {
                      let value = e.target.value.toUpperCase()
                      if (
                        value.match(
                          /^[ABCEGHJKLMNPRSTVXY]?[0-9]?[ABCEGHJKLMNPRSTVWXYZ]?[ -]?[0-9]?[ABCEGHJKLMNPRSTVWXYZ]?[0-9]?$/
                        ) ||
                        value === ""
                      ) {
                        field.onChange(value)
                      }
                    }}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <h3>{t("external.members.register_to_activities_online")}</h3>
        <p>
          {t("external.members.register_to_activities_online_msg", {
            lettersCase: "none",
          })}
        </p>
        <Row
          justify="space-between"
          gutter={32}
          style={{ marginTop: "var(--size-3)" }}
        >
          <Col xs={24} md={12}>
            <Form.Item
              validateStatus={errors.password && "error"}
              help={errors.password?.message}
              name="password"
              label={t("auth.password")}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("auth.password")}
                    size="large"
                    type="password"
                    {...field}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              validateStatus={errors.password_confirmation && "error"}
              help={errors.password_confirmation?.message}
              name="password_confirmation"
              label={t("auth.password_confirmation")}
            >
              <Controller
                name="password_confirmation"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder={t("auth.password")}
                    size="large"
                    type="password"
                    {...field}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Button
          htmlType="submit"
          block
          type="primary"
          loading={createMember.isLoading}
          size="large"
          style={{ marginTop: "var(--size-2)" }}
        >
          {t("global.submit")}
        </Button>
      </Form>
    </Card>
  )
}

function Success({
  hasPassword,
  isMember,
}: {
  hasPassword: boolean
  isMember: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <Card
      style={{
        overflow: "hidden",
        maxWidth: "70ch",
      }}
    >
      <header>
        <img src={laparoliereLogo} alt="laparoliere" />
        <h1 className={`headline-heavy ${styles.banner}`}>
          {t("external.members.register")}
        </h1>
      </header>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "24px 0",
        }}
      >
        <svg
          viewBox="64 64 896 896"
          focusable="false"
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <path
            d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"
            fill="var(--color-success)"
          />
          <path
            d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372zm193.4 225.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.3 0 19.9 5 25.9 13.3l71.2 98.8 157.2-218c6-8.4 15.7-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.4 12.7z"
            fill="white"
          />
          <path
            d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z"
            fill="var(--color-success)"
          />
        </svg>
      </div>
      <h2 className="header-heavy" style={{ textAlign: "center" }}>
        {t("external.members.registration_completed")}
      </h2>
      <Space
        style={{
          margin: "var(--size-4) 0",
          maxWidth: "70ch",
          textAlign: "start",
        }}
        direction="vertical"
        align="start"
      >
        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
          {t("external.members.registration_completed_msg_1", {
            lettersCase: "none",
          })}
        </Typography.Text>
        {!hasPassword && (
          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
            {t("external.members.registration_completed_msg_2", {
              lettersCase: "none",
            })}
          </Typography.Text>
        )}
        {isMember && (
          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
            {t("external.members.registration_completed_msg_3", {
              lettersCase: "none",
            })}
          </Typography.Text>
        )}
        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
          {t("external.members.registration_completed_msg_4", {
            lettersCase: "none",
          })}
        </Typography.Text>
      </Space>
      <Space align="center" direction="vertical" style={{ width: "100%" }}>
        <Button
          style={{ alignSelf: "center" }}
          type="primary"
          onClick={() => navigate("/")}
          size="large"
        >
          {t("global.my_portal")}
        </Button>
      </Space>
    </Card>
  )
}

function IsVerifying() {
  const { t } = useTranslation()
  return (
    <div>
      <Header>{t("auth.verifying")}</Header>
      <div className={styles.loader}>
        <ThreeDotsLoader />
      </div>
    </div>
  )
}

function Error({ error = "link_error" }: { error?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <Result
      status="error"
      title={t("errors.error")}
      subTitle={t(`external.members.${error}`, {
        lettersCase: "none",
      })}
      extra={[
        <Button
          type="primary"
          onClick={() => navigate("../email-registration")}
          size="large"
        >
          {t("global.go_back")}
        </Button>,
      ]}
    />
  )
}

export default Registration
